// Constants
import { PersonMentionSchema, AppMentionSchema, AtMentionItemClassName, MentionObjectIdAttribute, MentionItemTypeAttribute, MentionItemIndexAttribute, AtMentionMeItemClassName } from "@ms/uno-constants/lib/local/ChatConstants";
// Models
import { ChatMentionType } from "@ms/planner-models/lib/local/client/planner/chat/IChatMessage";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { createTrustedHtml } from "@ms/uno-utilities/lib/local/TrustedTypeUtilities";
/**
 * Serialize mentions in an HTML message string.
 * The "itemid" attribute map each mention element to the corresponding index in the mentions list.
 * User display names are stripped for compliance reasons.
 *
 * Rendered HTML format:
 * <span class="at-mention-item" mention-obj-id="user123" itemtype="https://schema.skype.com/Mention/Person">John Doe</span>
 *
 * Serialized HTML format:
 * <span itemtype="https://schema.skype.com/Mention/Person" itemid="0"></span>
 *
 * Mention object:
 * {
 *   index: 0,
 *   mentioned: "user123",
 *   type: ChatMentionType.User,
 * }
 * @param html The HTML message with at-mention elements
 * @returns An object containing the HTML message with serialized mentions and a list of mentions
 */ export function serializeEmbeddedHtmlMentions(html, traceLogger) {
    // Skip sanitization as it will be sanitized before sending and trusted html sanitization wipes off the attributes we need
    const trustedHtmlString = createTrustedHtml(html, true);
    const doc = new DOMParser().parseFromString(trustedHtmlString, "text/html");
    const mentions = [];
    // Find all at-mention elements and replace them with their storage format
    const atMentionElements = doc.querySelectorAll(`span.${AtMentionItemClassName}`);
    atMentionElements.forEach((element, index)=>{
        const mentionObjId = element.getAttribute(MentionObjectIdAttribute);
        const schemaValue = element.getAttribute(MentionItemTypeAttribute);
        if (!mentionObjId || !schemaValue) {
            return;
        }
        const mentionType = getMentionTypeBySchema(schemaValue);
        if (!mentionType) {
            traceLogger?.logTrace(0x1e1c2395 /* tag_4hcov */ , TraceLevel.Warning, `Unknown mention schema value`);
            return;
        }
        const span = doc.createElement("span");
        span.setAttribute(MentionItemTypeAttribute, schemaValue);
        span.setAttribute(MentionItemIndexAttribute, index.toString());
        if (schemaValue !== PersonMentionSchema) {
            // Persist display name for non-person mentions
            // Person mentions should not keep display name in storage for compliance
            span.textContent = element.textContent;
        }
        element.replaceWith(span);
        // Add to the mentions list
        mentions.push({
            index: index,
            mentioned: mentionObjId,
            type: mentionType
        });
    });
    // Return the modified HTML as a string
    return {
        messageContent: doc.body.innerHTML,
        mentions: mentions
    };
}
/**
 * Deserialize mentions in an HTML document by replacing them with elements that will be rendered to the user. See serializeEmbeddedHtmlMentions for the serialization format.
 * @param doc The HTML document with at-mention placeholders
 * @param mentions The list of mentions to replace in the content
 * @param currentUserId The ID of the current user, used to highlight mentions of themselves
 * @param getUserById Function to retrieve user data by ID and whether user is being fetched
 * @returns The HTML document with at-mention placeholders replaced by rendered user elements
 */ export function deserializeEmbeddedHtmlMentions(doc, mentions, currentUserId, getUserById) {
    // Replace placeholders with actual mention text
    mentions.forEach((mention)=>{
        const mentionElement = doc.querySelector(`span[${MentionItemIndexAttribute}="${mention.index}"]`);
        if (!mentionElement) {
            return;
        }
        const isCurrentUser = mention.mentioned === currentUserId;
        if (mention.type === ChatMentionType.User) {
            const { data: user, isFetching } = getUserById(mention.mentioned);
            // TODO polish: loading icon when fetching
            // If not fetching and user is not found, show as "Unknown user"
            const displayName = isFetching ? "" : user?.displayName ?? Strings.TaskChat_UnknownUser;
            const userMentionElement = createMentionRenderedElement(mention.mentioned, displayName, PersonMentionSchema, isCurrentUser);
            mentionElement.replaceWith(userMentionElement);
        } else if (mention.type === ChatMentionType.Application) {
            // For application mentions, try to get name by ID. Otherwise fallback to whatever is already stored in the message
            const appMentionElement = createMentionRenderedElement(mention.mentioned, getMentionedAppDisplayName(mention.mentioned) ?? (mentionElement.textContent || Strings.TaskChat_UnknownApplication), AppMentionSchema, isCurrentUser);
            mentionElement.replaceWith(appMentionElement);
        }
    });
    return doc;
}
/**
 * Creates a rendered element for a mention in the chat.
 * @param id The ID of the item being mentioned
 * @param displayName The display name of the item
 * @param itemType The type of the item being mentioned (e.g., PersonMentionSchema or AppMentionSchema)
 * @param isCurrentUser Whether the mentioned item is the current user
 * @returns An HTML span element representing the mentioned item.
 * Example: <span class="at-mention-item" mention-obj-id="id" itemtype="itemType" contenteditable="false" spellcheck="false">displayName</span>
 */ export function createMentionRenderedElement(id, displayName, itemType, isCurrentUser) {
    const element = document.createElement("span");
    element.className = isCurrentUser ? AtMentionMeItemClassName : AtMentionItemClassName;
    element.setAttribute(MentionObjectIdAttribute, id);
    element.setAttribute(MentionItemTypeAttribute, itemType);
    element.setAttribute("contenteditable", "false"); // Makes the element non-editable, behaves like a single entity
    element.setAttribute("spellcheck", "false");
    element.textContent = displayName;
    return element;
}
/**
 * Gets the mention type based on the schema string
 * @param schema The schema identifier of the mention (e.g., PersonMentionSchema or AppMentionSchema).
 */ export function getMentionTypeBySchema(schema) {
    switch(schema){
        case PersonMentionSchema:
            return ChatMentionType.User;
        case AppMentionSchema:
            return ChatMentionType.Application;
        default:
            return null;
    }
}
/**
 * Gets the display name for a mentioned application based on its ID.
 * @param appId The ID of the application being mentioned.
 */ export function getMentionedAppDisplayName(appId) {
    switch(appId){
        // No known applications for now
        default:
            return undefined;
    }
}
/**
 * Get the ID for a mention user list item by index
 * @param index The index of the list item
 */ export const getMentionUserListItemId = (index)=>{
    return `mention-user-${index}`;
};
/**
 * Get suggested users for the mention picker based on various criteria.
 * @param mentionableUsers List of all users that can be mentioned.
 * @param recentMessages List of recent chat messages to prioritize users.
 * @param prioritizedUserIds List of user IDs to prioritize.
 * @param filterText Text to filter users by.
 * @param numResults Maximum number of results to return.
 * @returns List of suggested users.
 */ export const getSuggestedUsersForMentionPicker = (mentionableUsers, recentMessages, prioritizedUserIds, filterText, numResults)=>{
    const suggestedUsers = [];
    const addedUserIds = new Set();
    const mentionableUserMap = mentionableUsers.reduce((map, user)=>{
        map[user.id] = user;
        return map;
    }, {});
    const lowerFilterText = filterText.toLowerCase();
    // Helper function to check if user matches filter text
    const matchesFilter = (user)=>{
        if (!filterText) {
            return true;
        }
        return user.displayName.toLowerCase().includes(lowerFilterText) || user.userPrincipalName.toLowerCase().includes(lowerFilterText) || (user.mail ?? "").toLowerCase().includes(lowerFilterText);
    };
    // Helper function to add user if not already added and matches filter
    const tryAddUserId = (userId)=>{
        const user = mentionableUserMap[userId];
        if (!user || addedUserIds.has(user.id) || !matchesFilter(user)) {
            return false;
        }
        suggestedUsers.push(user);
        addedUserIds.add(user.id);
        return true;
    };
    // 1. First, add users from recent messages (most recent first)
    // Sort messages by creation date (most recent first) and extract unique user IDs
    const recentUserIds = Array.from(new Set(recentMessages.sort((a, b)=>b.createdDateTime.epochMilliseconds - a.createdDateTime.epochMilliseconds).map((message)=>message.createdBy.user.id)));
    for (const userId of recentUserIds){
        if (suggestedUsers.length >= numResults) {
            break;
        }
        tryAddUserId(userId);
    }
    // 2. Next, add prioritized users
    for (const userId of prioritizedUserIds){
        if (suggestedUsers.length >= numResults) {
            break;
        }
        tryAddUserId(userId);
    }
    // 3. Finally, add remaining mentionable users in any order
    for (const user of mentionableUsers){
        if (suggestedUsers.length >= numResults) {
            break;
        }
        tryAddUserId(user.id);
    }
    return suggestedUsers;
};
